//copied from https://github.com/livechat/chat-window/blob/master/packages/chat-widget/src/iframe/components/Loader.tsx and slightly modified
import { keyframes, useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useFocusVisible } from '@react-aria/interactions'
import React from 'react'

const getDrawAnimation = (animationFactor: number) => keyframes`
  0% {
    stroke-dasharray: ${animationFactor * 22} ${animationFactor * 1000};
  }
  50% {
    stroke-dasharray: ${animationFactor * 88} ${animationFactor * 1000};
  }
  100% {
    stroke-dasharray: ${animationFactor * 22} ${animationFactor * 1000};
  }
`
const rotate = keyframes`
  0% {
    transform: rotate(-90deg);
  }
  100% {
    transform: rotate(270deg);
  }
`

const fill = keyframes`
  0% {
    stroke-dasharray: 1 1000;
  }
 100% {
    stroke-dasharray: 100 1000;
  }
`

const AnimatedCircle = styled.circle<{ animationFactor: number }>`
	animation-duration: 1s;
	transform-origin: center;
	animation-name: ${({ animationFactor }) => getDrawAnimation(animationFactor)}, ${rotate};
	animation-iteration-count: infinite;
	animation-timing-function: linear;
	stroke: #000;
`

const ProgressiveCircle = styled.circle`
	stroke-dasharray: 130;
	transform-origin: center;
	transform: rotate(-90deg);
	transition: stroke-dashoffset 100ms ${({ theme }) => theme.transitions.easings.motion.linear};
	stroke: #000;
`

const AnimatedProgressiveCircle = styled.circle<{ duration: number; delay: number }>`
	transform: rotate(-90deg);
	stroke-dasharray: 1 100;
	animation-duration: ${({ duration }) => duration}ms;
	animation-delay: ${({ delay }) => delay}ms;
	transform-origin: center;
	animation-name: ${fill};
	animation-timing-function: linear;
	animation-fill-mode: forwards;
	stroke: #000;
`

const circleProps: React.SVGProps<SVGCircleElement> = {
	r: '28',
	cx: '32',
	cy: '32',
	fill: 'none',
	strokeLinecap: 'round',
}

type Props = React.SVGProps<SVGSVGElement> & {
	size: 'small' | 'medium' | 'large' | 'xlarge'
	ariaLabel?: string
	progress?: number | { type: 'auto'; delay: number; duration: number }
}

const sizes = {
	small: {
		sideLength: 16,
		strokeWidth: 2,
	},
	medium: {
		sideLength: 24,
		strokeWidth: 3,
	},
	large: {
		sideLength: 32,
		strokeWidth: 4,
	},
	xlarge: {
		sideLength: 56,
		strokeWidth: 4,
	},
}

const CircularProgress = ({ size, progress }: Pick<Props, 'progress' | 'size'>) => {
	const { sideLength, strokeWidth } = sizes[size]
	const fillFactor = 56

	if (!progress) {
		return (
			<AnimatedCircle
				{...circleProps}
				strokeWidth={strokeWidth}
				vectorEffect="non-scaling-stroke"
				animationFactor={sideLength / fillFactor}
			/>
		)
	}
	if (typeof progress === 'number') {
		return <ProgressiveCircle {...circleProps} strokeWidth={strokeWidth} vectorEffect="non-scaling-stroke" />
	}
	if (progress.type === 'auto') {
		return (
			<AnimatedProgressiveCircle
				{...circleProps}
				strokeWidth={strokeWidth}
				vectorEffect="non-scaling-stroke"
				duration={progress.duration}
				delay={progress.delay}
			/>
		)
	}
	return null
}

const CircularLoader = ({ size, ariaLabel, progress, ...restProps }: Props) => {
	const theme = useTheme()
	const { sideLength, strokeWidth } = sizes[size]
	// we need to make reference to svg <title> tag using aria-labelledby attribute
	// some browsers require it
	const ariaProperties = ariaLabel ? { 'aria-labelledby': 'loader-label' } : {}
	const focusVisibleProps = useFocusVisible()

	return (
		<svg
			height={`${sideLength}px`}
			width={`${sideLength}px`}
			viewBox="0 0 64 64"
			role="img"
			{...ariaProperties}
			{...focusVisibleProps}
			{...restProps}
		>
			{ariaLabel && <title id="loader-label">{ariaLabel}</title>}
			<circle
				{...circleProps}
				strokeWidth={strokeWidth}
				stroke={theme.colorPalette.grayscale[300]}
				vectorEffect="non-scaling-stroke"
			/>
			<CircularProgress progress={progress} size={size} />
		</svg>
	)
}

export default CircularLoader
